import { $t } from '@/app-bootstrapping/SetUpInternationalization'

// The API returns these placeholders in English regardless of locale
const NO_NAME_PLACEHOLDER = '(No name)'
const INACTIVE_PROFILE_PLACEHOLDER = 'Inactive profile'

/** Whether the API sent a stand-in such as "(No name)" in place of the worker's real name. */
export function isWorkerNamePlaceholder(name: string | null | undefined): boolean {
  return name === NO_NAME_PLACEHOLDER || name === INACTIVE_PROFILE_PLACEHOLDER
}

type WorkerDisplayTitleInput = {
  name: string | null | undefined
  jobTitle: string | null | undefined
  email: string | null | undefined
}

/**
 * Translates the worker name if it's a placeholder like "(No name)" or "Inactive profile".
 * The API returns these in English regardless of locale,
 * so this function handles the translation for display.
 *
 * @param name - The worker name from the API
 * @returns The translated name or the original name
 */
export function getWorkerDisplayName(name: string | null | undefined): string {
  if (name === NO_NAME_PLACEHOLDER) {
    return $t('(No name)')
  }
  if (name === INACTIVE_PROFILE_PLACEHOLDER) {
    return $t('Inactive profile')
  }
  return name ?? ''
}

/**
 * Gets the display title for a worker.
 * Shows the job title when it's available, otherwise shows the email.
 *
 * @param worker - The worker object with name, jobTitle, and email
 * @param fallbackTitle - Optional fallback title when jobTitle is empty (default: undefined, returns jobTitle as-is)
 */
export function getWorkerDisplayTitle(
  worker: WorkerDisplayTitleInput,
  fallbackTitle?: string,
): string {
  // Show email instead of job title when worker hasn't provided a name
  if (worker.name === NO_NAME_PLACEHOLDER && worker.email) {
    return worker.email
  }

  if (!worker.jobTitle && fallbackTitle !== undefined) {
    return fallbackTitle
  }

  return worker.jobTitle ?? ''
}
